import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, MapPin, Pencil, ExternalLink, Loader2, RotateCcw, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { searchAi } from "@/lib/api";

/**
 * Ask Contacts AI — a right-side conversational drawer.
 *
 * State model:
 *   - messages: chat transcript [{role, text, results?, appliedFilters?, degraded?}]
 *   - currentResultIds: the *live candidate set* the next user turn will refine
 *     against (null on brand-new conversation). Each backend call passes this
 *     as pre_filtered_ids so hard filters compose across turns.
 *   - conversationHistory: compact [{role, text}] sent to the LLM so it can
 *     resolve pronouns/follow-ups like "only Ontario" or "exclude the UK ones".
 *
 * The drawer NEVER mutates the main Dashboard filters — it maintains its own
 * conversational scope. "Show on map" is a temporary map-filter passed up to
 * the parent so the underlying manual filters stay intact.
 */
export default function AskContactsAiDrawer({
  open,
  onOpenChange,
  contactsById,
  manuallyFilteredIds,
  activeManualFilterLabels,
  onOpenContact,
  onEditContact,
  onShowContactOnMap,
  onShowResultsOnMap,
}) {
  const [messages, setMessages] = useState([]);
  const [currentResultIds, setCurrentResultIds] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const startOver = () => {
    setMessages([]);
    setCurrentResultIds(null);
  };

  const sendTurn = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || busy) return;

    // Local slash-commands
    const lower = text.toLowerCase();
    if (["new search", "start over", "reset", "clear"].includes(lower)) {
      startOver();
      setInput("");
      return;
    }
    if (["show on map", "show them on the map", "show them on map", "show on the map"].includes(lower)) {
      const ids = currentResultIds || [];
      if (ids.length === 0) {
        setMessages((m) => [
          ...m,
          { role: "user", text },
          {
            role: "assistant",
            text:
              "There are no active results to show — try a search first, e.g. \"Find all GILFs in Canada\".",
          },
        ]);
        setInput("");
        return;
      }
      onShowResultsOnMap?.(ids);
      setMessages((m) => [
        ...m,
        { role: "user", text },
        {
          role: "assistant",
          text: `Showing ${ids.length} result${ids.length === 1 ? "" : "s"} on the map.`,
        },
      ]);
      setInput("");
      return;
    }

    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);

    // Build compact history for the backend (last few turns, no result blobs)
    const historySoFar = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, text: m.text.slice(0, 400) }));

    // Determine pre-filter set for THIS turn:
    // - If we have live results, refine within them (follow-up mode).
    // - Otherwise fall back to whatever manual dashboard filters are active.
    const preIds =
      currentResultIds && currentResultIds.length > 0
        ? currentResultIds
        : manuallyFilteredIds && manuallyFilteredIds.length > 0
        ? manuallyFilteredIds
        : null;

    try {
      const data = await searchAi(text, preIds, historySoFar);
      const results = data.results || [];
      const ids = results.map((r) => r.contact_id);
      setCurrentResultIds(ids);
      const summary =
        results.length === 0
          ? "No contacts match that. Try refining or type 'new search' to start over."
          : `${results.length} match${results.length === 1 ? "" : "es"}${
              data.applied_filters && data.applied_filters.length
                ? " · " + data.applied_filters.join(" · ")
                : ""
            }`;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: summary,
          results,
          appliedFilters: data.applied_filters || [],
          degraded: !!data.degraded,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "AI search is temporarily unavailable. Try again in a moment — normal search and filters still work.",
          degraded: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[520px] bg-[#0a0f14] border-white/10 text-white p-0 flex flex-col"
        data-testid="ai-drawer"
      >
        <SheetHeader className="px-4 py-3 border-b border-white/10 flex-row items-center gap-2 space-y-0">
          <Sparkles className="h-4 w-4 text-[#00e5ff]" />
          <SheetTitle className="text-white font-mono uppercase tracking-wider text-sm">
            Ask Contacts AI
          </SheetTitle>
          <div className="ml-auto flex items-center gap-2">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={startOver}
                title="Start over"
                className="text-[10px] font-mono uppercase tracking-wider text-white/60 hover:text-white flex items-center gap-1 px-2 py-1 border border-white/15 rounded"
                data-testid="ai-drawer-reset"
              >
                <RotateCcw className="h-3 w-3" /> New Search
              </button>
            )}
          </div>
        </SheetHeader>

        {activeManualFilterLabels && activeManualFilterLabels.length > 0 && (
          <div
            className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-white/50 bg-black/40 border-b border-white/10"
            data-testid="ai-drawer-scope-note"
          >
            Scoped to active dashboard filters: {activeManualFilterLabels.join(" · ")}
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && !busy && (
            <div className="text-white/50 text-sm">
              <div className="mb-2 font-mono uppercase tracking-wider text-[10px] text-[#00e5ff]">
                Try…
              </div>
              <ul className="space-y-1.5 text-sm">
                {[
                  "Find all GILFs in Canada",
                  "Of those, who will video?",
                  "Only Ontario",
                  "Show them on the map",
                ].map((s, i) => (
                  <li
                    key={i}
                    className="cursor-pointer hover:text-white bg-black/30 border border-white/10 rounded px-3 py-2"
                    onClick={() => sendTurn(s)}
                    data-testid={`ai-drawer-example-${i}`}
                  >
                    “{s}”
                  </li>
                ))}
              </ul>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              message={m}
              contactsById={contactsById}
              onOpen={onOpenContact}
              onEdit={onEditContact}
              onShowOnMap={onShowContactOnMap}
              onShowAllOnMap={() =>
                onShowResultsOnMap?.(m.results?.map((r) => r.contact_id) || [])
              }
            />
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-xs font-mono">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
            </div>
          )}
        </div>

        <div className="px-3 py-3 border-t border-white/10 bg-black/40">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  sendTurn(input);
                }
              }}
              placeholder={
                currentResultIds && currentResultIds.length > 0
                  ? "Refine these results…"
                  : "Ask about your contacts…"
              }
              className="flex-1 bg-black/50 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00e5ff]/50"
              data-testid="ai-drawer-input"
              disabled={busy}
              autoFocus
            />
            <button
              type="button"
              onClick={() => sendTurn(input)}
              disabled={busy || !input.trim()}
              className="h-9 w-9 rounded bg-[#00e5ff] text-black hover:bg-[#00e5ff]/80 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="ai-drawer-send"
              aria-label="Send"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
          {currentResultIds && currentResultIds.length > 0 && (
            <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-white/40">
              Refining {currentResultIds.length} contact{currentResultIds.length === 1 ? "" : "s"} · type "new search" to start over
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageBubble({
  message,
  contactsById,
  onOpen,
  onEdit,
  onShowOnMap,
  onShowAllOnMap,
}) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[95%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? "bg-[#00e5ff]/15 border border-[#00e5ff]/30 text-white"
            : "bg-black/50 border border-white/10 text-white/90"
        }`}
      >
        <div className="whitespace-pre-wrap">{message.text}</div>
        {message.results && message.results.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.results.slice(0, 8).map((r) => {
              const c = contactsById.get(r.contact_id);
              if (!c) return null;
              const loc = [c.town, c.region, c.country].filter(Boolean).join(" · ");
              const hasLoc = Number.isFinite(c.lat) && Number.isFinite(c.lng);
              return (
                <div
                  key={r.contact_id}
                  className="flex gap-2 p-2 rounded border border-white/10 bg-black/30"
                  data-testid={`ai-drawer-result-${r.contact_id}`}
                >
                  {c.avatar ? (
                    <img
                      src={c.avatar}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover border border-white/20 shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-white/5 border border-white/20 flex items-center justify-center text-[#f5d300] text-xs font-semibold shrink-0">
                      {(c.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-white truncate text-xs">
                        {c.name}
                      </div>
                      <span className="text-[9px] font-mono text-white/40">
                        {Math.round((r.score || 0) * 100)}%
                      </span>
                    </div>
                    {loc && (
                      <div className="text-[10px] font-mono text-white/50 truncate">
                        {loc}
                      </div>
                    )}
                    {r.reasons && r.reasons.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[10px] text-white/70">
                        {r.reasons.slice(0, 3).map((rr, i) => (
                          <li key={i} className="leading-snug">
                            <span className="text-[#00e5ff] mr-1">•</span>
                            {rr}
                          </li>
                        ))}
                      </ul>
                    )}
                    {r.snippet && (
                      <div className="mt-1 text-[10px] italic text-white/50 border-l border-white/20 pl-2">
                        “{r.snippet}”
                      </div>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <button
                        type="button"
                        onClick={() => onOpen?.(c)}
                        className="h-6 px-2 text-[9px] font-mono uppercase tracking-wider bg-transparent border border-white/15 text-white/80 hover:text-white rounded"
                        data-testid={`ai-drawer-open-${r.contact_id}`}
                      >
                        <ExternalLink className="h-2.5 w-2.5 inline mr-0.5" />
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit?.(c)}
                        className="h-6 px-2 text-[9px] font-mono uppercase tracking-wider bg-transparent border border-white/15 text-white/80 hover:text-white rounded"
                      >
                        <Pencil className="h-2.5 w-2.5 inline mr-0.5" />
                        Edit
                      </button>
                      {hasLoc && (
                        <button
                          type="button"
                          onClick={() => onShowOnMap?.(c)}
                          className="h-6 px-2 text-[9px] font-mono uppercase tracking-wider bg-transparent border border-[#00e5ff]/40 text-[#00e5ff] hover:text-white hover:border-[#00e5ff] rounded"
                          data-testid={`ai-drawer-map-${r.contact_id}`}
                        >
                          <MapPin className="h-2.5 w-2.5 inline mr-0.5" />
                          Map
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {message.results.length > 8 && (
              <div className="text-[10px] font-mono text-white/50 text-center">
                + {message.results.length - 8} more…
              </div>
            )}
            {message.results.length > 0 && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={onShowAllOnMap}
                  className="w-full text-[10px] font-mono uppercase tracking-wider text-[#00e5ff] hover:text-white px-2 py-1.5 border border-[#00e5ff]/40 rounded"
                  data-testid="ai-drawer-show-results-on-map"
                >
                  Show {message.results.length} result{message.results.length === 1 ? "" : "s"} on map
                </button>
              </div>
            )}
          </div>
        )}
        {message.degraded && (
          <div className="mt-1 text-[10px] text-amber-300/80">
            AI service is temporarily unavailable.
          </div>
        )}
      </div>
    </div>
  );
}
